import { memo, FC, useEffect, useState, useRef, useMemo, useCallback, RefObject, MouseEvent } from 'react';
import classNames from 'classnames';
import { isEmpty, isNil, noop } from 'lodash';
// Import UI Components
import { Tooltip, Popover, Button, Icon } from '@geneui/components';
// Import Components
import OptionList from './OptionList';
// Import Context
import { useMenuItemContentRefs } from 'components/MenuItemDropdown/context';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';

interface Props {
    placeholder?: string;
    optionLabel?: string;
    tooltipText?: string;
    popoverClassName?: string;
    buttonClassName?: string;
    buttonWrapperClassName?: string;
    popoverMinHeight?: number;
    popoverMaxHeight?: number;
    contentWrapperClassName?: string;
    openIcon?: string;
    closeIcon?: string;
    clearable?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange?: (e: any) => void;
    isMultiselect?: boolean;
    disabled?: boolean;
    onClear?: () => void;
    isValid?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errorText?: any;
    popoverAlign?: string;
    extendTargetWidth?: boolean;
    depthLevel: number;
    index: number;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

const OptionDropdown: FC<Props> = (props) => {
    const {
        placeholder = '',
        optionLabel = '',
        tooltipText = '',
        popoverClassName = '',
        buttonClassName = '',
        buttonWrapperClassName = '',
        contentWrapperClassName = '',
        popoverMinHeight = 332,
        popoverMaxHeight = 352,
        openIcon = 'bc-icon-arrow-down',
        closeIcon = 'bc-icon-arrow-up',
        clearable = true,
        isMultiselect = false,
        disabled = false,
        onClear = noop,
        onChange = noop,
        isValid = true,
        errorText = '',
        popoverAlign = 'center',
        extendTargetWidth = false,
        depthLevel,
        index,
        ...restProps
    } = props;

    const [isVisiblePopover, setIsVisiblePopover] = useState(false);

    const optionListRef = useRef(null);
    const toggleButtonRef = useRef(null);
    const { refs, setRefs } = useMenuItemContentRefs();

    useEffect(() => {
        setRefs((prevRefs) => ({
            ...prevRefs,
            [depthLevel]: {
                ...(prevRefs[depthLevel] ?? {}),
                [index]: [optionListRef, toggleButtonRef],
            },
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refsArr = useMemo(() => {
        return [
            optionListRef,
            toggleButtonRef,
            ...(Object.entries(refs)
                .filter(([key]) => +key > depthLevel)
                .map(([, refs]) => Object.values(refs))
                .flat(Infinity) as RefObject<HTMLDivElement>[]),
        ];
    }, [depthLevel, refs]);

    useOnClickOutside(
        refsArr,
        useCallback(() => {
            if (isVisiblePopover) {
                setIsVisiblePopover(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isVisiblePopover]),
    );

    const handleValueChange = (e) => {
        onChange(e);
        if (!isMultiselect) {
            setIsVisiblePopover(false);
        }
    };

    const togglePopover = () => {
        setIsVisiblePopover((prev) => !prev);
    };

    const handleClearClick = (e: MouseEvent) => {
        e.stopPropagation();
        onClear();
        setIsVisiblePopover(false);
    };

    return (
        <div className="crm-option-dropdown">
            <Popover
                isOpen={isVisiblePopover}
                extendTargetWidth={extendTargetWidth}
                className={classNames('crm-option-list-popover option-dropdown-popover-container', {
                    [popoverClassName]: popoverClassName,
                })}
                minHeight={popoverMinHeight}
                maxHeight={popoverMaxHeight}
                scrollbarNeeded={false}
                align={popoverAlign}
                disableReposition={false}
                Content={
                    <OptionList
                        onChange={handleValueChange}
                        isMultiselect={isMultiselect}
                        depthLevel={depthLevel}
                        {...restProps}
                    />
                }
                contentRef={optionListRef}
            >
                <div
                    className={classNames('crm-option-list-content-wrapper', {
                        [contentWrapperClassName]: contentWrapperClassName,
                        disabled: disabled,
                    })}
                >
                    <div className={buttonWrapperClassName} ref={toggleButtonRef}>
                        <Tooltip text={tooltipText} position={'bottom'}>
                            <Button
                                flexibility="default"
                                className={classNames('crm-option-list-content__toggle-button', {
                                    [buttonClassName]: buttonClassName,
                                    'crm-option-list-content__is-not-valid': !isValid,
                                })}
                                icon={isVisiblePopover ? closeIcon : openIcon}
                                onClick={togglePopover}
                            >
                                {isEmpty(optionLabel)
                                    ? !isNil(placeholder) && <div className="option-placeholder">{placeholder}</div>
                                    : optionLabel}
                                {clearable && !isEmpty(optionLabel) && (
                                    <Icon type="bc-icon-clear-small" onClick={handleClearClick} />
                                )}
                            </Button>
                        </Tooltip>
                        <span className="crm-option-list-content-wrapper__error-text">{errorText}</span>
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default memo(OptionDropdown);
